<script lang="ts" setup>
import type { CategoryTree } from "~~/models/categories";
import type { CategoryFilter } from "~~/models/filters";
import type { CatalogPageRecord } from "~~/server/graphql/datocms/generated";
import type { PlanktonCategoryWithSlugs } from "~~/server/plankton";
import { withQuery } from "ufo";

const { categoryTree, category } = defineProps<{
  cmsData: CatalogPageRecord;
  categoryFilters: Array<CategoryFilter>;
  categoryTree: CategoryTree;
  category?: PlanktonCategoryWithSlugs | null | undefined;
}>();

const localePath = useLocalePath();
const { path } = useRoute();
const { loggedIn } = useUserSession();

const { searchState, pagination } = useCatalog(
  {
    initialize: {
      searchState: false,
      catalog: false,
      ssr: !loggedIn.value,
      scrollIntoViewThreshold: 500,
    },
    params: {
      category: category,
    },
  },
);

function getLink(categoryId: number) {
  const prefix = path.includes("/happy-deals") ? "/happy-deals" : "/catalog";
  const categoryUrl = localePath(`${prefix}${categoryTree.getPath(categoryId)}`);

  if (!searchState.value.term?.length) {
    return categoryUrl;
  }

  return withQuery(categoryUrl, {
    term: searchState.value.term,
  });
}
</script>

<template>
  <div class="overflow-fade overflow-x-auto">
    <div class="gap-md flex lg:flex-wrap">
      <UButton variant="solid" color="primary" size="md" :ui="{ base: 'hover:bg-primary whitespace-nowrap hover:cursor-default' }">
        {{ cmsData.overviewLabelAll }} ({{ pagination.totalItems }})
      </UButton>

      <div v-for="categoryFilter of categoryFilters.filter((cf: CategoryFilter) => categoryTree.getName(cf.categoryId)?.length)" :key="categoryFilter.categoryId">
        <UButton variant="outline" color="primary" size="md" :ui="{ base: 'whitespace-nowrap' }" :to="getLink(categoryFilter.categoryId)">
          {{ categoryTree.getName(categoryFilter.categoryId) }} ({{ categoryFilter.productCount }})
        </UButton>
      </div>
    </div>
  </div>
</template>

<style scoped>
@property --left-fade {
  syntax: "<length>";
  inherits: false;
  initial-value: 0;
}

@property --right-fade {
  syntax: "<length>";
  inherits: false;
  initial-value: 0;
}

@keyframes scrollfade {
  0% {
    --left-fade: 0;
  }
  10%,
  100% {
    --left-fade: 3rem;
  }
  0%,
  90% {
    --right-fade: 3rem;
  }
  100% {
    --right-fade: 0;
  }
}

.overflow-fade {
  -webkit-mask: linear-gradient(to right, #0000, #ffff var(--left-fade) calc(100% - var(--right-fade)), #0000);
  mask: linear-gradient(to right, #0000, #ffff var(--left-fade) calc(100% - var(--right-fade)), #0000);
  animation: scrollfade 1s linear both;
  animation-timeline: --scrollfade;
  scroll-timeline: --scrollfade x;
}
</style>
